import { XCircleIcon } from '@heroicons/react/24/solid'
import Button from 'components/Button'
import { Combobox } from 'components/Form/Inputs/Combobox'
import ModalBody from 'components/Modal/ModalBody'
import ModalFooter from 'components/Modal/ModalFooter'
import UrlModal from 'components/Modal/UrlModal'
import { Pill } from 'components/Pill'
import { useGetItemsQuery } from 'containers/items/queries'
import { useGetRecipeCategoriesQuery } from 'containers/recipeCategories/queries'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useUrlFilter } from 'utils/routing/useUrlFilter'

export function Filters() {
    const navigate = useNavigate()

    const { search } = useLocation()

    const [ingredientSearch, setIngredientSearch] = useState('')

    const { filter: filterCategories, setFilter: setFilterCategories } = useUrlFilter('categories')
    const { filter: filterIngredients, setFilter: setFilterIngredients } = useUrlFilter('includes_ingredients')

    const { data: itemsData, isLoading: isItemsLoading } = useGetItemsQuery()
    const { data: recipeCategoriesData, isLoading: isRecipeCategoriesLoading } = useGetRecipeCategoriesQuery()

    function goBack() {
        navigate(`/recipes${search}`)
    }

    const safeFilterCategories = filterCategories?.value ?? []
    const safeFilterIngredients = filterIngredients?.value ?? []

    return (
        <div>
            <UrlModal title='Recipe Filters' onClose={goBack} loading={isItemsLoading || isRecipeCategoriesLoading}>
                <>
                    <ModalBody>
                        {(() => {
                            if (!itemsData || !recipeCategoriesData) {
                                return null
                            }

                            return (
                                <>
                                    <div>
                                        <div className='flex justify-between items-center w-full'>
                                            <h4>Categories</h4>
                                            {safeFilterCategories.length > 0 && (
                                                <button
                                                    type='button'
                                                    className='text-sky-500 hover:text-sky-600 hover:underline'
                                                    onClick={() => setFilterCategories({ type: '$in', value: [] })}
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                        <div className='flex flex-wrap gap-2 mt-2'>
                                            <button
                                                type='button'
                                                onClick={() => {
                                                    setFilterCategories({
                                                        type: '$in',
                                                        value: []
                                                    })
                                                }}
                                            >
                                                <Pill unselected={safeFilterCategories.length !== 0}>All categories</Pill>
                                            </button>
                                            {(recipeCategoriesData ?? []).map(({ id, name }) => {
                                                const isActive = safeFilterCategories.includes(id.toString())

                                                return (
                                                    <button
                                                        type='button'
                                                        key={id}
                                                        onClick={() => {
                                                            if (isActive) {
                                                                setFilterCategories({
                                                                    type: '$in',
                                                                    value: safeFilterCategories.filter((prevCatId) => prevCatId !== id.toString())
                                                                })
                                                            } else {
                                                                setFilterCategories({
                                                                    type: '$in',
                                                                    value: [...safeFilterCategories, id.toString()]
                                                                })
                                                            }
                                                        }}
                                                    >
                                                        <Pill unselected={!isActive}>{name}</Pill>
                                                    </button>
                                                )
                                            })}
                                            <button
                                                type='button'
                                                onClick={() => {
                                                    if (safeFilterCategories.includes('Uncategorized')) {
                                                        setFilterCategories({
                                                            type: '$in',
                                                            value: safeFilterCategories.filter((prevCatId) => prevCatId !== 'Uncategorized')
                                                        })
                                                    } else {
                                                        setFilterCategories({
                                                            type: '$in',
                                                            value: [...safeFilterCategories, 'Uncategorized']
                                                        })
                                                    }
                                                }}
                                            >
                                                <Pill unselected={!safeFilterCategories.includes('Uncategorized')}>Uncategorized</Pill>
                                            </button>
                                        </div>
                                    </div>
                                    <div className='mt-4 min-h-72'>
                                        <div className='flex justify-between items-center w-full'>
                                            <h4>Ingredients</h4>
                                            {safeFilterIngredients.length > 0 && (
                                                <button
                                                    type='button'
                                                    className='text-sky-500 hover:text-sky-600 hover:underline'
                                                    onClick={() => setFilterIngredients({ type: '$in', value: [] })}
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                        <div className='mt-2'>
                                            <Combobox
                                                isFullWidth
                                                value={ingredientSearch}
                                                setValue={setIngredientSearch}
                                                options={itemsData.map(({ name, id }) => ({ value: name, id }))}
                                                placeholder='Show recipes that include...'
                                                onOptionSelect={(value) => {
                                                    setFilterIngredients({ type: '$in', value: [...new Set([...safeFilterIngredients, value])] })
                                                    setIngredientSearch('')
                                                }}
                                            />
                                            <div className='flex flex-col gap-2 mt-2'>
                                                {safeFilterIngredients.map((ing) => {
                                                    return (
                                                        <div key={ing} className='flex gap-2'>
                                                            <p>{ing}</p>
                                                            <button
                                                                type='button'
                                                                onClick={() =>
                                                                    setFilterIngredients({
                                                                        type: '$in',
                                                                        value: safeFilterIngredients.filter((sfi) => sfi !== ing)
                                                                    })
                                                                }
                                                            >
                                                                <XCircleIcon className='w-6 text-sky-500' />
                                                            </button>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )
                        })()}
                    </ModalBody>
                    <ModalFooter
                        buttons={[
                            <Button key={2} onClick={goBack}>
                                Apply
                            </Button>
                        ]}
                    />
                </>
            </UrlModal>
        </div>
    )
}
